import React from 'react';

import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet';

import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, Container, Stack } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef, GridPagination } from '@mui/x-data-grid';

import PageHeader from '~/components/Common/Elements/PageHeader';

import useAppNavigation from '~/hooks/useAppNavigation';

import CorporateEventsService, { Company } from '~/services/CorporateEventsService';

export default function CompaniesListPage() {
  const { push } = useAppNavigation();

  const {
    data: companies = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['corporate-events', 'companies'],
    queryFn: () => CorporateEventsService.getCompanies(),
  });

  const handleEdit = (id: string) => {
    push(`/corporate-events/companies/${id}/edit`);
  };

  const handleCreate = () => {
    push('/corporate-events/companies/create');
  };

  const handleBackToEvents = () => {
    push('/corporate-events/events');
  };

  const columns: Array<GridColDef<Company>> = [
    {
      field: 'name',
      headerName: 'Company Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleEdit(params.id as string)}
        />,
      ],
    },
  ];

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Companies</title>
      </Helmet>

      <PageHeader title="Companies">
        <Stack direction="row" spacing={2}>
          <Button variant="text" startIcon={<ArrowBackIcon />} onClick={handleBackToEvents}>
            Back to Events
          </Button>
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleCreate}>
            Create Company
          </Button>
        </Stack>
      </PageHeader>

      <Box mt={4}>
        {error && (
          <Box mb={2} p={2} sx={{ backgroundColor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
            Error loading companies: {error instanceof Error ? error.message : 'Unknown error'}
          </Box>
        )}

        <DataGrid
          loading={isLoading}
          rows={companies}
          columns={columns}
          pagination
          paginationModel={{ page: 0, pageSize: 25 }}
          pageSizeOptions={[10, 25, 50, 100]}
          getRowId={(row) => row.id}
          slots={{ pagination: GridPagination }}
          autoHeight
          disableRowSelectionOnClick
        />
      </Box>
    </Container>
  );
}
